import React from 'react';

/**
 * File: BillyIcon.tsx
 * Description: Shell-branded "Billy" icon used in the TopBar. Billy is the
 *   business-facing chat UI for exploring project feasibility and cost. All
 *   variants inherit colour from `currentColor` (Shell Red in the TopBar).
 * Author: Archie Frontend
 */

interface BillyIconProps {
  /** Pixel size of the square icon. Defaults to 26. */
  size?: number;
  className?: string;
}

/** Shared SVG wrapper so the icon has consistent sizing/colour behaviour. */
const IconShell: React.FC<{
  size: number;
  className?: string;
  children: React.ReactNode;
}> = ({ size, className, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

/**
 * Shell-branded Billy icon: a friendly assistant head with an antenna and chat
 * smile, conveying the business advisor for project feasibility & cost.
 *
 * @param size Square pixel size of the icon. Defaults to 26.
 * @param className Optional class applied to the root SVG.
 * @return The rendered icon element.
 */
export const BillyIcon: React.FC<BillyIconProps> = ({
  size = 26,
  className,
}) => (
  <IconShell size={size} className={className}>
    {/* Assistant head */}
    <rect x="4" y="7" width="16" height="11.5" rx="4" />
    {/* Antenna */}
    <path d="M12 3.5V7" />
    <circle cx="12" cy="2.8" r="1" fill="currentColor" stroke="none" />
    {/* Side "ears" */}
    <path d="M2.5 11.5v3M21.5 11.5v3" />
    {/* Eyes + smile */}
    <path d="M9.2 11.8h.01M14.8 11.8h.01" />
    <path d="M9.5 15c.7.6 1.6.9 2.5.9s1.8-.3 2.5-.9" />
  </IconShell>
);

export default BillyIcon;



